import type { OpenAPIAwaitedResponse } from '../api/api-switcher'

/**
 * Extract trace ID from response headers
 * @param response - The Response object from the API call
 * @returns The trace ID if available, undefined otherwise
 */
export const getTraceIdFromResponse = (response: Response): string | undefined => {
  return response.headers.get('X-Request-Id') || undefined
}

/**
 * Format error message with trace ID
 * @param baseMessage - The base error message (e.g., from i18n)
 * @param traceId - The trace ID from the response
 * @returns Formatted error message including trace ID
 */
export const formatErrorWithTraceId = (baseMessage: string, traceId?: string): string => {
  if (!traceId) return baseMessage
  return `${baseMessage} (Trace ID: ${traceId})`
}

/**
 * Extract error info including trace ID from an API response
 * @param apiResponse - The API response object
 * @returns Object containing error message and trace ID
 */
export const extractErrorInfo = <T>(apiResponse: OpenAPIAwaitedResponse<T>): {
  message: string
  traceId?: string
} => {
  const { error, response } = apiResponse

  if (!error) {
    return { message: '' }
  }

  const traceId = getTraceIdFromResponse(response)

  return {
    message: error.message || 'An error occurred',
    traceId
  }
}